import CookieManager from "../../components/Cookies/CookieManager";
import Dashboard from "../../layout/Dashboard/Dashboard";
import "./home.scss";
import React, { useEffect, useMemo, useState } from "react";
import { getAllDatasets, getDataset } from "../../services/datasetsService";

// 👉 NEW: country router component
import CountryDashboard from "./CountryDashboard";

const FEATURED_COUNTRIES = ["Belgium", "Greece", "Spain"];
const CRC_SCREENING_POLICIES_ID = "68de4b8033a11410a81e7c86";

const HomePage = () => {
  const [country, setCountry] = useState("Belgium");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [allDashboardMetas, setAllDashboardMetas] = useState([]);
  const [detailsById, setDetailsById] = useState({});
  const [screeningPolicies, setScreeningPolicies] = useState([]);

  // Fetch all datasets (keep only category === "Dashboard")
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const all = await getAllDatasets();
        const dashboards = (all || []).filter(
          (d) => d?.category === "Dashboard"
        );
        if (!cancelled) setAllDashboardMetas(dashboards);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to fetch datasets.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch CRC Screening Policies dataset
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const payload = await getDataset(CRC_SCREENING_POLICIES_ID, 0, 60000);
        if (!cancelled) {
          setScreeningPolicies(payload?.elements || []);
        }
      } catch (e) {
        console.error("Failed to fetch CRC Screening Policies:", e);
        // Don't set error state - this is non-critical data
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Filter by selected country (country name included in datasetName)
  const countryMetas = useMemo(() => {
    const name = (country || "").toLowerCase();
    return (allDashboardMetas || []).filter((d) =>
      (d?.datasetName || "").toLowerCase().includes(name)
    );
  }, [allDashboardMetas, country]);

  // Get screening policy for selected country
  const countryPolicy = useMemo(() => {
    return screeningPolicies.find(
      (policy) =>
        (policy?.Country || "").toLowerCase() === (country || "").toLowerCase()
    );
  }, [screeningPolicies, country]);

  // Get other countries (not featured) in alphabetical order
  const otherCountries = useMemo(() => {
    return screeningPolicies
      .map((policy) => policy?.Country)
      .filter((c) => c && !FEATURED_COUNTRIES.includes(c))
      .sort((a, b) => a.localeCompare(b));
  }, [screeningPolicies]);

  // Fetch details for selected country's datasets
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!countryMetas.length) {
        setDetailsById({});
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const results = await Promise.all(
          countryMetas.map(async (m) => {
            const payload = await getDataset(m.id, 0, 60000);
            return [m.id, payload];
          })
        );
        if (!cancelled) {
          const map = {};
          results.forEach(([id, payload]) => {
            map[id] = payload;
          });
          setDetailsById(map);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || "Failed to fetch dataset data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [countryMetas]);

  return (
    <div>
      <CookieManager />
      <Dashboard>
        <div className="page-wrap">
          <div className="controls">
            <h2 className="title">Please select your country</h2>
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="country-select"
            >
              <optgroup label="Featured Countries">
                {FEATURED_COUNTRIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </optgroup>
              {otherCountries.length > 0 && (
                <optgroup label="Other Countries">
                  {otherCountries.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

          {loading && <p style={{ opacity: 0.8 }}>Loading…</p>}
          {error && <p style={{ color: "#e57373", marginTop: 8 }}>{error}</p>}
          {!loading && !error && countryMetas.length === 0 && !countryPolicy && (
            <p>No data found for {country}.</p>
          )}

          {/* 👉 NEW: render country-specific dashboard */}
          {!loading && !error && (countryMetas.length > 0 || countryPolicy) && (
            <CountryDashboard
              country={country}
              metas={countryMetas}
              dataById={detailsById}
              policyData={countryPolicy}
            />
          )}
        </div>
      </Dashboard>
    </div>
  );
};

export default HomePage;
